import React from 'react';
import { motion } from 'framer-motion';
import { FiCheck, FiShield, FiZap } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const plans = [
  {
    name: 'Starter',
    price: '$500',
    per: '/month',
    tagline: 'Best for small teams testing AI support',
    features: [
      'Up to 50 calls/day (~1,500/month)',
      '1 AI voice agent',
      'Basic CRM integration (1 platform)',
      'Inbound call handling only',
      'Weekly performance reports',
      'Setup fee: Waived during pilot',
    ],
    cta: 'Start Free Pilot',
    highlight: false,
  },
  {
    name: 'Growth',
    price: '$1,000',
    per: '/month',
    tagline: 'Best for growing teams needing after-hours & overflow coverage',
    features: [
      'Up to 150 calls/day (~4,500/month)',
      '2 AI voice agents (multi-department)',
      'Full CRM + helpdesk integration (up to 3 platforms)',
      'Inbound + outbound calling',
      'Call recording, transcripts, analytics',
      'Weekly optimization & reporting',
      'Dedicated account manager',
      'Setup fee: Waived during pilot',
    ],
    cta: 'Start Free Pilot',
    highlight: true,
  },
  {
    name: 'Enterprise',
    price: '$2,000',
    per: '/month',
    tagline: 'Best for companies replacing or augmenting full call center operations',
    features: [
      'Unlimited calls',
      'Unlimited AI voice agents',
      'Full integration suite (CRM, helpdesk, calendar, custom APIs)',
      'Inbound + outbound + proactive notifications',
      'Custom voice, scripts, and workflows',
      'Priority support + direct founder access',
      'Advanced analytics and custom reporting',
      'Setup fee: Waived during pilot',
    ],
    cta: 'Contact Us',
    highlight: false,
  },
];

const Pricing = () => {
  const scrollToBooking = () => {
    document.getElementById('booking-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="pricing-section" className="py-12 md:py-16 bg-slate-900 text-white relative overflow-hidden">
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-green-600/10 rounded-full blur-[100px] -mr-20 -mt-20 pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-slate-600/10 rounded-full blur-[100px] -ml-20 -mb-20 pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="bg-green-700 rounded-2xl p-6 mb-10 text-center max-w-4xl mx-auto">
          <div className="flex items-center justify-center gap-2 mb-2">
            <SafeIcon icon={FiZap} className="w-5 h-5 text-white" />
            <span className="font-bold text-lg">14-Day Pilot Program</span>
          </div>
          <p className="text-green-100 text-sm md:text-base">
            Try any plan free for 14 days. No setup fee. No commitment. See it handle real calls before you pay a dollar.
          </p>
        </div>

        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-bold mb-3">Simple, Transparent Pricing</h2>
          <p className="text-slate-400 max-w-2xl mx-auto">No setup fees. No hidden costs. Cancel anytime.</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 items-start max-w-6xl mx-auto mb-12">
          {plans.map((plan, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className={`rounded-3xl p-6 md:p-8 border shadow-2xl relative ${plan.highlight
                ? 'bg-gradient-to-br from-green-700 to-green-800 border-green-500'
                : 'bg-slate-800 border-slate-700'
              }`}
            >
              {plan.highlight && (
                <div className="absolute top-0 right-0 bg-white text-green-700 text-xs font-bold px-4 py-1.5 rounded-bl-xl rounded-tr-2xl">
                  MOST POPULAR
                </div>
              )}
              <h3 className="text-2xl font-bold mb-2 mt-2">{plan.name}</h3>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-4xl md:text-5xl font-bold">{plan.price}</span>
                <span className={plan.highlight ? 'text-green-200' : 'text-slate-400'}>{plan.per}</span>
              </div>
              <p className={`text-sm mb-6 ${plan.highlight ? 'text-green-200' : 'text-slate-400'}`}>{plan.tagline}</p>

              <div className="space-y-3 mb-8">
                {plan.features.map((item, j) => (
                  <div key={j} className="flex items-start gap-3">
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${plan.highlight ? 'bg-white/20' : 'bg-slate-700'}`}>
                      <SafeIcon icon={FiCheck} className="w-3 h-3 text-white" />
                    </div>
                    <span className={`text-sm leading-relaxed ${plan.highlight ? 'text-white' : 'text-slate-300'}`}>{item}</span>
                  </div>
                ))}
              </div>

              <button
                onClick={scrollToBooking}
                className={`w-full py-3 rounded-xl font-bold transition-colors ${plan.highlight
                  ? 'bg-white text-green-700 hover:bg-green-50'
                  : 'bg-slate-700 text-white hover:bg-slate-600'
                }`}
              >
                {plan.cta}
              </button>
            </motion.div>
          ))}
        </div>

        <div className="bg-slate-800 border-2 border-slate-700 rounded-2xl p-6 flex flex-col md:flex-row gap-4 max-w-4xl mx-auto items-start md:items-center">
          <SafeIcon icon={FiShield} className="w-10 h-10 text-green-400 flex-shrink-0" />
          <div>
            <h4 className="font-bold text-white text-lg mb-1">14-Day Pilot + 30-Day Money-Back Guarantee</h4>
            <p className="text-slate-300 text-sm leading-relaxed">
              You have 44 days to see this work before it costs you anything permanent. Try free for 14 days, then get a full refund within 30 days if it's not delivering results.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;